/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Modal, Form, SingleDatePicker, Divider } from '@mairistem/composants';

import { SOUS_TYPE_ACTE_OPTIONS, TYPE_ACTE_OPTIONS } from '@/constants';
import { useActes } from '@/hooks';
import type { Acte, TypeActe } from '@/types';
import { stringToAPIDateFormat } from '@/utils';

import ActeTable from './ActeTable';

interface Props {
  /**
   * Called when an acte is selected
   */
  onSelectedActe?: (acte: Acte) => void;
  /**
   * Boolean to know if the modal is open
   */
  open: boolean;
  /**
   * Function to close the modal
   */
  onClose: () => void;
}

const SelectionActeModal = (props: Props) => {
  const { onSelectedActe, open, onClose } = props;

  const [typeActe, setTypeActe] = React.useState<TypeActe>('naissance');
  // Form.Dropdown is not an input so the form doesn't see it. So we store its value here.
  const [sousTypeActe, setSousTypeActe] = React.useState<string>('');
  const [page, setPage] = React.useState(1);
  const [params, setParams] = React.useState({});

  const actes = useActes(typeActe);

  const [dateEventField, nomClassementField] = React.useMemo(() => {
    switch (typeActe) {
      case 'naissance':
        return ['dateNaissance', 'nomClassementEnfant'];

      case 'reconnaissance':
        return ['dateReconnaissancePere', 'nomClassementEnfant'];

      case 'deces':
        return ['dateDeces', 'nomClassementDefunt'];

      case 'mariage':
        return ['dateMariage', 'conjoint1'];

      default:
        return ['', ''];
    }
  }, [typeActe]);

  const handleTypeActeChange = React.useCallback(
    (
      _e: React.ChangeEvent<HTMLInputElement>,
      data: Record<string, TypeActe>,
    ) => {
      setTypeActe(data.value as TypeActe);
      actes.reset();
      setSousTypeActe('');
    },
    [actes.reset],
  );

  const handleSousTypeActeChange = React.useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, data: Record<string, string>) => {
      setSousTypeActe(data.value);
    },
    [],
  );

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formData = new FormData(event.target as HTMLFormElement);
      const data = Object.fromEntries(formData);
      const search = {
        ...data,
        ...(typeActe !== 'reconnaissance'
          ? { codeTypeActe: sousTypeActe }
          : { codeSousTypeActe: sousTypeActe }),
        ...(!_.isEmpty(data?.conjoint1)
          ? { conjoint1: `contains(${data?.conjoint1})` }
          : {}),
        ...(!_.isEmpty(data?.nomClassementDefunt)
          ? { nomClassementDefunt: `contains(${data?.nomClassementDefunt})` }
          : {}),
        ...(!_.isEmpty(data?.nomClassementEnfant)
          ? { nomClassementEnfant: `contains(${data?.nomClassementEnfant})` }
          : {}),
      };

      if (!_.isEmpty(params[dateEventField])) {
        params[dateEventField] = stringToAPIDateFormat(params[dateEventField]);
      }

      setParams(search);
      setPage(1);

      actes.reset();
      actes.get(search);
    },
    [sousTypeActe, actes.get, actes.reset, dateEventField, params, typeActe],
  );

  const handleSelected = React.useCallback(
    (acte: Acte) => {
      if (_.isFunction(onSelectedActe)) {
        onSelectedActe({ ...acte, type: typeActe });
      }
    },
    [onSelectedActe, typeActe],
  );

  const handleScroll = ({ currentTarget }) => {
    const { scrollHeight, scrollTop, clientHeight } = currentTarget;

    if (
      scrollHeight - scrollTop <= clientHeight &&
      page < actes.meta.lastPage
    ) {
      actes.get({
        ...params,
        page: page + 1,
      });
      setPage((prev) => prev + 1);
    }
  };

  return (
    <Modal closeIcon open={open} onClose={onClose} size="large">
      <Modal.Header content="Veuillez choisir un acte" />
      <Modal.Content>
        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Select
              width={6}
              required
              label="Type d'acte"
              options={TYPE_ACTE_OPTIONS}
              placeholder="Choississez le type d'acte"
              value={typeActe}
              onChange={handleTypeActeChange}
            />
            <Form.Dropdown
              width={10}
              clearable
              selection
              label="Sous-type d'acte"
              placeholder="Choississez le sous-type d'acte"
              name="sousTypeActe"
              options={SOUS_TYPE_ACTE_OPTIONS[typeActe]}
              value={sousTypeActe}
              onChange={handleSousTypeActeChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              width={7}
              name={nomClassementField}
              label="Recherche NOM Prénom"
              placeholder="Saisissez le NOM et le Prénom à rechercher"
            />
            <Form.Input
              width={3}
              label="Année"
              placeholder="AAAA"
              name="anneeActe"
              maxLength="4"
            />
            <Form.Input
              width={3}
              label="Numéro"
              placeholder="XXXX"
              name="numeroActe"
              maxLength="4"
            />
            <Form.Field width={3}>
              <label>Date d&apos;événement</label>
              <SingleDatePicker
                name={dateEventField}
                clearable
                fluid
                placeholder="dd/mm/yyyy"
              />
            </Form.Field>
          </Form.Group>
          <Form.Button color="blue" content="Rechercher" type="submit" />
        </Form>
        <Divider />
        <div
          style={{ height: 350, overflowY: 'scroll' }}
          onScroll={handleScroll}
        >
          <ActeTable
            actes={actes.data}
            loading={actes.loading}
            typeActe={typeActe}
            onSelected={handleSelected}
          />
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default SelectionActeModal;
